import {
  actionColumn,
  Chips,
  column,
  Css,
  EditColumnsButton,
  FilterDefs,
  Filters,
  GridCellContent,
  GridColumn,
  GridTable,
  IconButton,
  multiFilter,
  simpleDataRows,
  SimpleHeaderAndData,
  Tag,
  useFilter,
  useGridTableApi,
  useModal,
} from "@homebound/beam";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { SearchBox } from "src/components";
import { Maybe } from "src/generated/graphqlTypes";
import { useNavigateToRpav } from "src/hooks/useNavigateToRpav";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import {
  PlanPackageOptionsV2_OptionFragment,
  PlanPackageOptionsV2_PlanPackageFragment,
  usePlanPackageOptionsV2Query,
  useSavePlanPackageOptionsV2Mutation,
} from "src/routes/libraries/plan-package-v2/components/OptionsTab.generated";
import { queryResult } from "src/utils";

type OptionsTabProps = { planPackageId: string; versionId: string };

export function OptionsTab(props: OptionsTabProps) {
  const { planPackageId, versionId } = props;
  const query = usePlanPackageOptionsV2Query({ variables: { id: planPackageId, versionId } });
  return queryResult(query, ({ planPackage }) => <DataView planPackage={planPackage} />);
}

type DataViewProps = { planPackage: PlanPackageOptionsV2_PlanPackageFragment };
type Row = SimpleHeaderAndData<PlanPackageOptionsV2_OptionFragment>;
type OptionsFilter = { type?: string[]; location?: Maybe<string>[] };

function DataView(props: DataViewProps) {
  const { planPackage } = props;
  const { openModal } = useModal();
  const navigateToRpav = useNavigateToRpav();
  const [save] = useSavePlanPackageOptionsV2Mutation();
  const api = useGridTableApi<Row>();
  const [search, setSearch] = useState<string>();
  const disabled = disableBasedOnPotentialOperation(planPackage.canEdit);
  const filterDefs = useMemo(() => createFilterDefs(planPackage.options), [planPackage]);
  const { filter, setFilter } = useFilter({ filterDefs });

  const openRemoveModal = useCallback(
    (rpo: PlanPackageOptionsV2_OptionFragment) =>
      openModal({
        content: (
          <ConfirmationModal
            danger
            title="Remove Option"
            label="Remove"
            confirmationMessage={`Are you sure you want to remove ${rpo.code} - ${rpo.name} from this plan?`}
            onConfirmAction={async () => {
              const { data } = await save({
                variables: { input: { id: planPackage.id, options: [{ id: rpo.id, active: false }] } },
              });
              const versionId = data?.savePlanPackage.planPackage.version.id;
              // Removing an option on a published version triggers a new draft, so navigate to it
              if (versionId && versionId !== planPackage.version.id) navigateToRpav(versionId);
            }}
          />
        ),
      }),
    [openModal, save, planPackage, navigateToRpav],
  );

  const columns = useMemo(() => createColumns(disabled, openRemoveModal), [disabled, openRemoveModal]);
  const rows = useMemo(() => simpleDataRows(applyFilter(planPackage.options, filter)), [planPackage, filter]);

  return (
    <div css={Css.p3.df.fdc.gap2.$}>
      <div>
        <h2 css={Css.xl.$}>Plan Options</h2>
        <div css={Css.pt1.$}>The options offered with this plan.</div>
      </div>
      <div css={Css.df.aic.jcsb.gap1.$}>
        <div css={Css.df.aic.gap1.$}>
          <SearchBox onSearch={setSearch} clearable updateQueryString={false} />
          <Filters filter={filter} filterDefs={filterDefs} onChange={setFilter} />
        </div>
        <EditColumnsButton api={api} columns={columns} placement="right" />
      </div>
      <GridTable
        api={api}
        columns={columns}
        rows={rows}
        filter={search}
        sorting={{ on: "client" }}
        fallbackMessage="No options yet"
      />
    </div>
  );
}

function createColumns(
  disabled: boolean | ReactNode,
  onRemove: (rpo: PlanPackageOptionsV2_OptionFragment) => void,
): GridColumn<Row>[] {
  return [
    column<Row>({ header: "Option Code", data: ({ code }) => code, w: "160px", canHide: false }),
    column<Row>({ header: "Option Name", data: ({ name }) => name, mw: "320px", canHide: false }),
    column<Row>({
      header: "Type",
      data: ({ type }) => ({ content: <Tag text={type.name} />, value: type.name }),
      name: "Type",
      id: "type",
    }),
    column<Row>({
      header: "Location",
      data: ({ location }) => location?.name ?? "None",
      name: "Location",
      id: "location",
    }),
    column<Row>({
      header: "Requires",
      data: (rpo) => optionCodesCell(rpo.optionPrerequisites),
      name: "Requires",
      id: "requires",
    }),
    column<Row>({
      header: "Conflicts with",
      data: (rpo) => optionCodesCell([...rpo.optionConflicts, ...rpo.optionConflictChildren].uniqueBy((o) => o.id)),
      name: "Conflicts with",
      id: "conflicts",
    }),
    column<Row>({ header: "Impact", data: (rpo) => impactCell(rpo), name: "Impact", id: "impact" }),
    actionColumn<Row>({
      header: "",
      data: (rpo) => (
        <IconButton
          icon="trash"
          disabled={disabled || disableBasedOnPotentialOperation(rpo.canDelete)}
          onClick={() => onRemove(rpo)}
        />
      ),
      w: "56px",
      clientSideSort: false,
      canHide: false,
    }),
  ];
}

function createFilterDefs(options: PlanPackageOptionsV2_OptionFragment[]): FilterDefs<OptionsFilter> {
  return {
    type: multiFilter({
      label: "Type",
      options: options
        .map((o) => o.type)
        .uniqueBy((t) => t.id)
        .sortBy((t) => t.name),
      getOptionLabel: ({ name }) => name,
      getOptionValue: ({ id }) => id,
    }),
    location: multiFilter({
      label: "Location",
      options: [
        { id: null, name: "None" },
        ...options
          .map((o) => o.location)
          .compact()
          .uniqueBy((l) => l.id)
          .sortBy((l) => l.name),
      ],
      getOptionLabel: ({ name }) => name,
      getOptionValue: ({ id }) => id,
    }),
  };
}

function applyFilter(options: PlanPackageOptionsV2_OptionFragment[], filter: OptionsFilter) {
  return options
    .filter((o) => !filter.type || filter.type.includes(o.type.id))
    .filter((o) => !filter.location || filter.location.includes(o.location?.id ?? null));
}

function optionCodesCell(options: Pick<PlanPackageOptionsV2_OptionFragment, "id" | "code">[]): GridCellContent {
  return {
    content: options.nonEmpty ? <Chips values={options.map((o) => o.code)} /> : "-",
    value: options.map((o) => o.code).join(" "),
  };
}

function impactCell(rpo: PlanPackageOptionsV2_OptionFragment): GridCellContent {
  // The program data row referencing only this option holds its base impact
  const pd = rpo.programDatas.find((rppd) => rppd.readyPlanOptions.length === 1)?.programData;
  const impacts = [impactLine(pd?.sellableSqft, "Sellable"), impactLine(pd?.netBuildableSqft, "Net")].compact();
  return {
    content: impacts.nonEmpty ? impacts.map((line) => <div key={line}>{line}</div>) : "-",
    value: impacts.join(", "),
  };
}

function impactLine(sqft: Maybe<number>, label: string): string | undefined {
  return sqft ? `${sqft > 0 ? "+" : ""}${sqft} sf (${label})` : undefined;
}
